import React from "react";
import {
  Row,
  Col,
  Typography,
  Image,
  Space,
  Avatar,
  Grid,
  Table,
  notification,
} from "antd";
import { CalendarOutlined, TagOutlined } from "@ant-design/icons";
import { usersAPI } from "@api";
import { useTranslation } from "react-i18next";
import { fallbackImage } from "@assets";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import "./MyClaims.styles.css";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const MyClaimsPage = () => {
  const { t, i18n } = useTranslation("common");
  const { t: tError } = useTranslation("error");
  const { md } = useBreakpoint();

  const { isLoading, data: claims } = useQuery({
    queryKey: ["myClaims"],
    queryFn: () => usersAPI.getMyClaims().then(({ data }) => data),
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const columns = [
    {
      title: t("claimTable.award"),
      dataIndex: "award",
      fixed: md ? "left" : "",
      width: 300,
      sorter: (a, b) => a.award.name.localeCompare(b.award.name),
      filteredValue: null,
      render: (award) => award.name,
      onFilter: (value, record) => {
        return (
          record.award.name.toLowerCase().includes(value.toLowerCase()) ||
          record.user.firstName.toLowerCase().includes(value.toLowerCase()) ||
          record.user.lastName.toLowerCase().includes(value.toLowerCase()) ||
          record.status.toLowerCase().includes(value.toLowerCase())
        );
      },
    },
    {
      title: t("claimTable.amount"),
      dataIndex: "award",
      width: 200,
      render: (award) => (
        <Space align="center" size={12} style={{ marginBottom: "15px" }}>
          <Avatar
            size="default"
            icon={<TagOutlined key="tag" style={{ color: "#2966c6" }} />}
            style={{ background: "#edeffb" }}
          />
          <Text
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#2966c6",
            }}
          >{`${award?.amount} ${t("marketplace.points")}`}</Text>
        </Space>
      ),
    },
    {
      title: t("claimTable.createdAt"),
      dataIndex: "createdAt",
      width: 210,
      sorter: (a, b) => dayjs(a.createdAt) - dayjs(b.createdAt),
      render: (date) => (
        <Text style={{ color: "#8C8C8C" }}>
          <CalendarOutlined style={{ fontSize: "16px", marginRight: "10px" }} />
          {dayjs(date).locale(language).format("lll")}
        </Text>
      ),
    },
    {
      title: t("claimTable.status"),
      dataIndex: "status",
      width: 120,
      render: (status) => (
        <Title
          align={md ? "left" : "center"}
          level={5}
          style={{
            display: "inline-flex",
            margin: md ? "0 0 15px" : "auto auto 15px",
          }}
        >
          {t(`status.${status}`)}
        </Title>
      ),
    },
    {
      title: t("prizeTable.imageUrl"),
      dataIndex: "award",
      width: 110,
      render: (award) => (
        <Image
          width={50}
          height={50}
          src={award?.imageUrl}
          alt={award?.name}
          fallback={fallbackImage}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          wrapperStyle={{
            width: "50px",
            height: "50px",
            overflow: "hidden",
            borderRadius: "5px",
          }}
        />
      ),
    },
    {
      title: t("claimTable.closed"),
      dataIndex: "closed",
      width: 210,
      sorter: (a, b) => dayjs(a.closed) - dayjs(b.closed),
      render: (date) => (
        <Text style={{ color: "#8C8C8C" }}>
          <CalendarOutlined style={{ fontSize: "16px", marginRight: "10px" }} />
          {date ? dayjs(date).locale(language).format("lll") : "-"}
        </Text>
      ),
    },
  ];

  const language = i18n.language.substring(0, 2);

  return (
    <>
      <Row justify="center" align="middle" className="container-row">
        <Col span={22}>
          <Typography.Title align="left" level={2} style={{ marginTop: 0 }}>
            {t("navbar.myClaims")}
          </Typography.Title>
        </Col>
        <Col
          span={22}
          align="center"
          style={{
            height: "100%",
            backgroundColor: "#fff",
          }}
        >
          <Table
            dataSource={claims ?? []}
            columns={columns}
            pagination={false}
            rowKey={"id"}
            scroll={{ x: "max-content", y: "calc(100vh - 335px)" }}
            loading={isLoading}
            borderRadius={false}
          />
        </Col>
      </Row>
    </>
  );
};

export default MyClaimsPage;
